import { brand } from '@metorial/brand';
import { Paths } from '@metorial/frontend-config';
import {
  CommandPalette,
  CommandPaletteTrigger,
  RailAccountMenu,
  RailLayout,
  SubNavBackLink,
  SubNavTitle,
  TopBarIconButton,
  useCommandPaletteHotkey,
  type CommandPaletteItem,
  type RailItem,
  type SubNavSpec
} from '@metorial/layout';
import { lastProductPaneByInstanceStore, redirectToLogout } from '@metorial/state';
import { Logo, Menu, theme } from '@metorial/ui';
import * as Popover from '@radix-ui/react-popover';
import {
  RiAddLine,
  RiBookOpenLine,
  RiCodeSSlashLine,
  RiCompassDiscoverLine,
  RiGroupLine,
  RiHome6Line,
  RiKey2Line,
  RiLinkM,
  RiPlugLine,
  RiQuestionLine,
  RiSettings3Line,
  RiSparkling2Line,
  RiUserAddLine,
  RiWebhookLine
} from '@remixicon/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useShellScope, type ShellScope } from './scope';
import {
  accountSubNav,
  developerSubNav,
  flattenSubNav,
  getProductSegments,
  getSection,
  integrationsSubNav,
  settingsSubNav,
  skillsSubNav,
  workforceSubNav,
  type ShellSection
} from './sections';
import { SettingsTopBar } from './settingsBar';
import { WorkspaceSwitcher } from './switcher';

// The BlackOS dashboard shell: icon rail, section sub-nav, top bar with the
// workspace breadcrumb, "Search in BlackOS" (⌘K), Help and the + create menu.
// Used by the product (/i/...), settings (/o/...) and account (/account) layouts.

/**
 * The + menu navigates to the page that owns each action with `?create=1`
 * (`?invite=1` for portal users); those pages open their dialog when they see it.
 */
export let CREATE_QUERY_PARAM = 'create';
export let INVITE_QUERY_PARAM = 'invite';

let paneSections: ShellSection[] = [
  'home',
  'integrations',
  'skills',
  'workforce',
  'developer'
];

let getPortalIdFromPath = (pathname: string) => {
  let segments = getProductSegments(pathname);
  return segments[0] == 'portal' ? segments[1] : undefined;
};

let buildSubNav = (
  section: ShellSection,
  scope: ShellScope,
  pathname: string,
  portalId: string | undefined
): SubNavSpec | null => {
  if (!scope.instance) {
    if (section == 'account') return accountSubNav(pathname, accountHeader(scope));
    return null;
  }

  switch (section) {
    case 'integrations':
      return integrationsSubNav(scope, pathname);
    case 'skills':
      return skillsSubNav(scope, pathname);
    case 'workforce':
      return workforceSubNav(scope, pathname, portalId);
    case 'developer':
      return developerSubNav(scope, pathname);
    case 'settings':
      return settingsSubNav(scope, pathname, settingsHeader(scope));
    case 'account':
      return accountSubNav(pathname, accountHeader(scope));
    default:
      return null;
  }
};

let backTo = (scope: ShellScope) =>
  scope.instance
    ? Paths.instance.home(scope.organization, scope.project, scope.instance)
    : '/';

// "‹ Back to BlackOS" and the project/environment selects sit in the settings
// top bar (settingsBar.tsx), like the cloud.
let settingsHeader = (scope: ShellScope) => (
  <>
    <div style={{ padding: '4px 2px 0' }}>
      <SubNavTitle>Settings</SubNavTitle>
      <SubNavTitle style={{ fontWeight: 500, color: theme.colors.gray650 }}>
        {scope.organization?.name}
      </SubNavTitle>
    </div>
  </>
);

let accountHeader = (scope: ShellScope) => (
  <>
    <SubNavBackLink to={backTo(scope)} label={`Back to ${brand.name}`} />
    <div style={{ padding: '10px 2px 0' }}>
      <SubNavTitle>{brand.name} Account</SubNavTitle>
    </div>
  </>
);

// Help ------------------------------------------------------------------------

let HelpContent = styled(Popover.Content)`
  z-index: 999;
  width: 360px;
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.gray400};
  border-radius: 14px;
  box-shadow: ${theme.shadows.large};
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

let HelpCard = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border: 1px solid ${theme.colors.gray300};
  border-radius: 10px;

  b {
    font-size: 14px;
    font-weight: 600;
    color: ${theme.colors.gray900};
  }

  small {
    font-size: 13px;
    color: ${theme.colors.gray650};
  }

  &:hover {
    background: ${theme.colors.gray200};
  }
`;

let HelpMenu = ({ scope }: { scope: ShellScope }) => {
  let [open, setOpen] = useState(false);
  let { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);

  let p = [scope.organization, scope.project, scope.instance] as const;
  let portalId = scope.defaultPortal?.id;

  let cards = [
    {
      title: 'Getting started with Workforce',
      body: 'Set up your first workforce and let it get to work.',
      to: portalId
        ? Paths.instance.workforcePortal.overview(...p, portalId)
        : Paths.instance.workforce(...p)
    },
    {
      title: 'Getting started with Portals',
      body: 'Let your users connect their own accounts.',
      to: portalId
        ? Paths.instance.workforcePortal.accounts(...p, portalId)
        : Paths.instance.workforce(...p)
    },
    {
      title: 'Getting started with Skills',
      body: 'Turn your tools into skills your agents can use.',
      to: Paths.instance.skills(...p)
    },
    {
      title: 'Setting up your first integration',
      body: 'Connect a provider and start calling its tools.',
      to: Paths.instance.providers(...p)
    },
    {
      title: `Building with the ${brand.name} API`,
      body: 'Authenticate and send your first API request.',
      to: Paths.instance.developer(...p, 'api-keys')
    }
  ];

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <TopBarIconButton type="button" aria-label="Help" title="Help">
          <RiQuestionLine />
        </TopBarIconButton>
      </Popover.Trigger>

      <Popover.Portal>
        <HelpContent side="bottom" align="end" sideOffset={8}>
          <b style={{ fontSize: 16 }}>Help</b>
          <small style={{ color: theme.colors.gray650, marginBottom: 4 }}>
            Getting started
          </small>
          {scope.instance &&
            cards.map(card => (
              <Link key={card.title} to={card.to}>
                <HelpCard>
                  <b>{card.title}</b>
                  <small>{card.body}</small>
                </HelpCard>
              </Link>
            ))}
        </HelpContent>
      </Popover.Portal>
    </Popover.Root>
  );
};

// + create menu -----------------------------------------------------------------

let useCreateActions = (scope: ShellScope) => {
  let navigate = useNavigate();
  let p = [scope.organization, scope.project, scope.instance] as const;
  let portalId = scope.defaultPortal?.id;

  let withParam = (to: string, param: string) => `${to}?${param}=1`;

  return [
    {
      id: 'new-integration',
      label: 'New Integration',
      icon: <RiPlugLine />,
      run: () => navigate(withParam(Paths.instance.integrations(...p), CREATE_QUERY_PARAM))
    },
    {
      id: 'new-skill',
      label: 'New Skill',
      icon: <RiSparkling2Line />,
      run: () => navigate(withParam(Paths.instance.skills(...p), CREATE_QUERY_PARAM))
    },
    {
      id: 'new-magic-mcp-server',
      label: 'New Magic MCP Server',
      icon: <RiWebhookLine />,
      run: () => navigate(withParam(Paths.instance.magicMcp.servers(...p), CREATE_QUERY_PARAM))
    },
    {
      // BlackOS: integrations come from the library or a remote MCP server URL.
      id: 'link-remote-mcp-server',
      label: 'Link Remote MCP Server',
      icon: <RiLinkM />,
      run: () =>
        navigate(withParam(Paths.instance.externalProviders(...p), CREATE_QUERY_PARAM))
    },
    {
      id: 'invite-portal-user',
      label: 'Invite Portal User',
      icon: <RiUserAddLine />,
      disabled: !portalId,
      run: () =>
        portalId &&
        navigate(
          withParam(
            Paths.instance.workforcePortal.accounts(...p, portalId),
            INVITE_QUERY_PARAM
          )
        )
    },
    {
      id: 'open-explorer',
      label: 'Open Explorer',
      icon: <RiCompassDiscoverLine />,
      run: () => navigate(Paths.instance.explorer(...p))
    }
  ];
};

let CreateMenu = ({ scope }: { scope: ShellScope }) => {
  let actions = useCreateActions(scope);
  if (!scope.instance) return null;

  return (
    <Menu
      label="Create"
      items={actions.map(a => ({
        id: a.id,
        label: a.label,
        icon: a.icon,
        disabled: a.disabled
      }))}
      onItemClick={id => actions.find(a => a.id == id)?.run()}
    >
      <TopBarIconButton
        type="button"
        data-variant="primary"
        aria-label="Create"
        title="Create"
      >
        <RiAddLine />
      </TopBarIconButton>
    </Menu>
  );
};

// Shell ------------------------------------------------------------------------

export let DashboardShell = ({ children }: { children: React.ReactNode }) => {
  let { pathname } = useLocation();
  let navigate = useNavigate();
  let section = getSection(pathname);
  let portalIdFromPath = section == 'workforce' ? getPortalIdFromPath(pathname) : undefined;
  let scope = useShellScope({ portalId: portalIdFromPath });
  let createActions = useCreateActions(scope);
  let [paletteOpen, setPaletteOpen] = useState(false);
  useCommandPaletteHotkey(setPaletteOpen);

  let { organization, project, instance } = scope;
  let p = [organization, project, instance] as const;

  useEffect(() => {
    if (!instance || !paneSections.includes(section)) return;
    lastProductPaneByInstanceStore
      .set(
        instance.id,
        section as 'home' | 'integrations' | 'skills' | 'workforce' | 'developer'
      )
      .catch(() => {});
  }, [instance?.id, section]);

  useEffect(() => {
    let name =
      section == 'settings'
        ? organization?.name
        : section == 'account'
          ? 'Account'
          : section == 'workforce'
            ? (scope.currentPortal?.name ?? project?.name)
            : project?.name;
    if (name) document.title = `${name} • ${brand.name}`;
  }, [section, organization?.name, project?.name, scope.currentPortal?.name]);

  let workforceTo = scope.defaultPortal
    ? Paths.instance.workforcePortal.overview(...p, scope.defaultPortal.id)
    : Paths.instance.workforce(...p);

  let skillsHidden = scope.flag('skills-enabled') === false;

  let railItems: RailItem[] = instance
    ? [
        {
          id: 'home',
          label: 'Home',
          icon: <RiHome6Line />,
          to: Paths.instance.home(...p),
          active: section == 'home'
        },
        {
          id: 'integrations',
          label: 'Integrations',
          icon: <RiPlugLine />,
          to: Paths.instance.integrationsOverview(...p),
          active: section == 'integrations'
        },
        ...(skillsHidden
          ? []
          : [
              {
                id: 'skills',
                label: 'Skills',
                icon: <RiSparkling2Line />,
                to: Paths.instance.skills(...p),
                active: section == 'skills'
              }
            ]),
        {
          id: 'workforce',
          label: 'Workforce',
          icon: <RiGroupLine />,
          to: workforceTo,
          active: section == 'workforce'
        },
        {
          id: 'developer',
          label: 'Developer',
          icon: <RiCodeSSlashLine />,
          to: Paths.instance.developer(...p),
          active: section == 'developer'
        },
        {
          id: 'explorer',
          label: 'Explorer',
          icon: <RiCompassDiscoverLine />,
          to: Paths.instance.explorer(...p),
          active: section == 'explorer'
        }
      ]
    : [];

  let bottomItems: RailItem[] = organization
    ? [
        {
          id: 'settings',
          label: 'Settings',
          icon: <RiSettings3Line />,
          to: Paths.organization.settings(organization),
          active: section == 'settings'
        }
      ]
    : [];

  let subNav = buildSubNav(
    section,
    scope,
    pathname,
    scope.currentPortal?.id ?? portalIdFromPath
  );

  let isSettings = section == 'settings';

  let paletteItems = useMemo((): CommandPaletteItem[] => {
    if (!paletteOpen || !instance) return [];

    // Settings has its own "Search settings…" over the settings pages only.
    if (isSettings) {
      return flattenSubNav(settingsSubNav(scope, pathname)).map(({ group, link }) => ({
        id: `page:${link.to}`,
        label: String(link.label),
        description: group ? `Settings › ${group}` : 'Settings',
        group: 'Settings',
        icon: link.icon ?? <RiSettings3Line />,
        keywords: ['settings'],
        onSelect: () => navigate(link.to)
      }));
    }

    let sections: { name: string; spec: SubNavSpec | null; icon: React.ReactNode }[] = [
      {
        name: 'Integrations',
        spec: integrationsSubNav(scope, pathname),
        icon: <RiPlugLine />
      },
      ...(skillsHidden
        ? []
        : [
            { name: 'Skills', spec: skillsSubNav(scope, pathname), icon: <RiSparkling2Line /> }
          ]),
      {
        name: 'Workforce',
        spec: workforceSubNav(scope, pathname, scope.defaultPortal?.id),
        icon: <RiGroupLine />
      },
      { name: 'Developer', spec: developerSubNav(scope, pathname), icon: <RiKey2Line /> },
      {
        name: 'Settings',
        spec: settingsSubNav(scope, pathname),
        icon: <RiSettings3Line />
      }
    ];

    let seen = new Set<string>();
    let pages: CommandPaletteItem[] = [
      {
        id: 'page:home',
        label: 'Home',
        group: 'Pages',
        icon: <RiHome6Line />,
        onSelect: () => navigate(Paths.instance.home(...p))
      }
    ];

    for (let s of sections) {
      for (let { group, link } of flattenSubNav(s.spec)) {
        if (seen.has(link.to)) continue;
        seen.add(link.to);
        pages.push({
          id: `page:${link.to}`,
          label: String(link.label),
          description: group ? `${s.name} › ${group}` : s.name,
          group: 'Pages',
          icon: s.icon,
          keywords: [s.name],
          onSelect: () => navigate(link.to)
        });
      }
    }

    let actions: CommandPaletteItem[] = createActions
      .filter(a => !a.disabled)
      .map(a => ({
        id: `action:${a.id}`,
        label: a.label,
        group: 'Create',
        icon: a.icon,
        onSelect: a.run
      }));

    let portals: CommandPaletteItem[] = scope.portals.map(portal => ({
      id: `portal:${portal.id}`,
      label: portal.name,
      description: 'Portal',
      group: 'Portals',
      icon: <RiBookOpenLine />,
      onSelect: () => navigate(Paths.instance.workforcePortal.overview(...p, portal.id))
    }));

    return [...actions, ...pages, ...portals];
  }, [
    paletteOpen,
    instance?.id,
    scope.portals,
    scope.defaultPortal?.id,
    skillsHidden,
    isSettings,
    scope.project?.id
  ]);

  let searchPlaceholder = isSettings ? 'Search settings...' : `Search in ${brand.name}`;

  return (
    <RailLayout
      logo={<Logo size={30} />}
      logoHref={instance ? Paths.instance.home(...p) : '/'}
      items={railItems}
      bottomItems={bottomItems}
      account={
        <RailAccountMenu
          user={scope.user.data}
          accountHref={Paths.account.profile()}
          onLogout={redirectToLogout}
          active={section == 'account'}
        />
      }
      topBar={
        isSettings
          ? {
              left: <SettingsTopBar scope={scope} />,
              right: (
                <>
                  <div style={{ width: 300, minWidth: 160, flexShrink: 1 }}>
                    <CommandPaletteTrigger
                      placeholder={searchPlaceholder}
                      onClick={() => setPaletteOpen(true)}
                    />
                  </div>
                  <HelpMenu scope={scope} />
                </>
              )
            }
          : {
              left: <WorkspaceSwitcher scope={scope} showPortal={section == 'workforce'} />,
              center: (
                <CommandPaletteTrigger
                  placeholder={searchPlaceholder}
                  onClick={() => setPaletteOpen(true)}
                />
              ),
              right: (
                <>
                  <HelpMenu scope={scope} />
                  <CreateMenu scope={scope} />
                </>
              )
            }
      }
      subNav={subNav}
    >
      {children}

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        items={paletteItems}
        placeholder={searchPlaceholder}
      />
    </RailLayout>
  );
};
